import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
// IMPORTANT: import safeToast BEFORE any other module that uses `sonner`
// so its global patch (which prevents React error #31 when toast.error
// is called with an object) is active for the entire app.
import '@/lib/safeToast';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LangProvider } from '@/i18n/LangContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';

// ─── Eager (critical path / auth / layouts / guards) ─────────────────────
import { InAppUpdateChecker } from '@/components/InAppUpdateChecker';
import Landing from '@/pages/Landing';
import MobileWelcome from '@/pages/MobileWelcome';
import { InstallAppPrompt } from '@/components/InstallAppPrompt';
import AppUpdateBanner from '@/components/AppUpdateBanner';
import AttendanceGate from '@/components/AttendanceGate';
import usePushNotifications from '@/hooks/usePushNotifications';
import CustomerAuth from '@/pages/CustomerAuth';
import AdminAuth from '@/pages/AdminAuth';
import CustomerLayout from '@/layouts/CustomerLayout';
import AdminLayout from '@/layouts/AdminLayout';
import IdleSessionManager from '@/components/IdleSessionManager';

// ─── Lazy (route pages) ──────────────────────────────────────────────────
// Phase 37 — Every page below is code-split so the initial JS payload
// stays small. React.lazy() ensures the page bundle only downloads when
// the route is hit; subsequent visits are cached.
const Privacy               = lazy(() => import('@/pages/Privacy'));
const SignupPendingApproval = lazy(() => import('@/pages/customer/SignupPendingApproval'));
const SignupKycPage         = lazy(() => import('@/pages/customer/SignupKycPage'));

// Public website (multi-page) — shared shell + pages
const SiteLayout            = lazy(() => import('@/components/site/SiteLayout'));
const ResidentialSolarPage  = lazy(() => import('@/pages/site/ResidentialSolarPage'));
const SolutionTypePage      = lazy(() => import('@/pages/site/SolutionTypePage'));
const SocietySolarPage      = lazy(() => import('@/pages/site/SocietySolarPage'));
const CommercialSolarPage   = lazy(() => import('@/pages/site/CommercialSolarPage'));
const SubsidyGuidePage      = lazy(() => import('@/pages/site/SubsidyGuidePage'));
const SolarCalculatorPage   = lazy(() => import('@/pages/site/SolarCalculatorPage'));
const ProductsPage          = lazy(() => import('@/pages/site/ProductsPage'));
const ProductDetailPage     = lazy(() => import('@/pages/site/ProductsPage').then(m => ({ default: m.ProductDetailPage })));
const PricingPage           = lazy(() => import('@/pages/site/PricingPage'));
const HowItWorksPage        = lazy(() => import('@/pages/site/HowItWorksPage'));
const WarrantyPage          = lazy(() => import('@/pages/site/WarrantyPage'));
const FinancingPage         = lazy(() => import('@/pages/site/FinancingPage'));
const WhyUPPowerPage        = lazy(() => import('@/pages/site/WhyUPPowerPage'));
const AboutPage             = lazy(() => import('@/pages/site/AboutPage'));
const ContactPage           = lazy(() => import('@/pages/site/ContactPage'));
const ServiceAreasPage      = lazy(() => import('@/pages/site/ServiceAreasPage'));
const FaqPage               = lazy(() => import('@/pages/site/FaqPage'));
const CareersPage           = lazy(() => import('@/pages/site/CareersPage'));
const BlogListPage          = lazy(() => import('@/pages/site/BlogPages'));
const BlogPostPage          = lazy(() => import('@/pages/site/BlogPages').then(m => ({ default: m.BlogPostPage })));
const CustomerStoriesPage   = lazy(() => import('@/pages/site/CustomerStoriesPage'));
const CityPage              = lazy(() => import('@/pages/site/CityPage'));

// Customer routes
const CustomerHome          = lazy(() => import('@/pages/customer/Home'));
const NewApplication        = lazy(() => import('@/pages/customer/NewApplication'));
const MyApplications        = lazy(() => import('@/pages/customer/MyApplications'));
const ServicesPage          = lazy(() => import('@/pages/customer/ServicesPage'));
const ApplicationDetail     = lazy(() => import('@/pages/customer/ApplicationDetail'));
const SupportPage           = lazy(() => import('@/pages/customer/SupportPage'));
const MyComplaints          = lazy(() => import('@/pages/customer/MyComplaints'));
const SubsidyInfo           = lazy(() => import('@/pages/customer/SubsidyInfo'));
const QuickLinks            = lazy(() => import('@/pages/customer/QuickLinks'));
const VendorList            = lazy(() => import('@/pages/customer/VendorList'));
const VendorChat            = lazy(() => import('@/pages/customer/VendorChat'));
const ProfilePage           = lazy(() => import('@/pages/customer/ProfilePage'));
const SolarInfoPage         = lazy(() => import('@/pages/customer/SolarInfoPage'));
const AIChatPage            = lazy(() => import('@/pages/customer/AIChatPage'));
const QuotationsPage        = lazy(() => import('@/pages/customer/QuotationsPage'));
const SiteSurveyPage        = lazy(() => import('@/pages/customer/SiteSurveyPage'));
const GenerationLogPage     = lazy(() => import('@/pages/customer/GenerationLogPage'));
const ReferPage             = lazy(() => import('@/pages/customer/ReferPage'));
const CustomerProjects      = lazy(() => import('@/pages/customer/CustomerProjects'));
const CustomerProjectDetail = lazy(() => import('@/pages/customer/CustomerProjectDetail'));
const BbpsPage              = lazy(() => import('@/pages/BbpsPage'));
const WalletPage            = lazy(() => import('@/pages/customer/WalletPage'));
const CustomerCreditReport  = lazy(() => import('@/pages/customer/CustomerCreditReport'));
const CustomerLoans         = lazy(() => import('@/pages/customer/CustomerLoans'));

// Admin routes
const AdminDashboard        = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminLeads            = lazy(() => import('@/pages/admin/AdminLeads'));
const AdminLeadDetail       = lazy(() => import('@/pages/admin/AdminLeadDetail'));
const AdminLeadConfig       = lazy(() => import('@/pages/admin/AdminLeadConfig'));
const AdminQuotations       = lazy(() => import('@/pages/admin/AdminQuotations'));
const AdminSaleInvoices     = lazy(() => import('@/pages/admin/AdminSaleInvoices'));
const AdminPaymentIns       = lazy(() => import('@/pages/admin/AdminPaymentIns'));
const AdminCashRegister     = lazy(() => import('@/pages/admin/AdminCashRegister'));
const AdminPaymentCenter    = lazy(() => import('@/pages/admin/AdminPaymentCenter'));
const AdminApplications     = lazy(() => import('@/pages/admin/AdminApplications'));
const AdminProjects         = lazy(() => import('@/pages/admin/AdminProjects'));
const AdminProjectDetail    = lazy(() => import('@/pages/admin/AdminProjectDetail'));
const AdminComplaints       = lazy(() => import('@/pages/admin/AdminComplaints'));
const AdminCareAuditLog     = lazy(() => import('@/pages/admin/AdminCareAuditLog'));
const AdminCustomers        = lazy(() => import('@/pages/admin/AdminCustomers'));
const AdminSubsidyEditor    = lazy(() => import('@/pages/admin/AdminSubsidyEditor'));
const AdminContentEditor    = lazy(() => import('@/pages/admin/AdminContentEditor'));
const AdminSiteSurveys      = lazy(() => import('@/pages/admin/AdminSiteSurveys'));
const AdminStaff            = lazy(() => import('@/pages/admin/AdminStaff'));
const AdminAccessBundles    = lazy(() => import('@/pages/admin/AdminAccessBundles'));
const AdminReferrals        = lazy(() => import('@/pages/admin/AdminReferrals'));
const AdminAttendance       = lazy(() => import('@/pages/admin/AdminAttendance'));
const AdminAttendanceSummary= lazy(() => import('@/pages/admin/AdminAttendanceSummary'));
const AdminProducts         = lazy(() => import('@/pages/admin/AdminProducts'));
const AdminSaathiBookings   = lazy(() => import('@/pages/admin/AdminSaathiBookings'));
const AdminStaffTracking    = lazy(() => import('@/pages/admin/AdminStaffTracking'));
const AdminNotifications    = lazy(() => import('@/pages/admin/AdminNotifications'));
const AdminCareerApplications = lazy(() => import('@/pages/admin/AdminCareerApplications'));
const AdminSystemErrors     = lazy(() => import('@/pages/admin/AdminSystemErrors'));
const AdminMessageOutbox    = lazy(() => import('@/pages/admin/AdminMessageOutbox'));
const WhatsAppInbox         = lazy(() => import('@/pages/admin/WhatsAppInbox'));
const AdminNerochatLogs     = lazy(() => import('@/pages/admin/AdminNerochatLogs'));
const AdminRazorpay         = lazy(() => import('@/pages/admin/AdminRazorpay'));
const AdminMobileApp        = lazy(() => import('@/pages/admin/AdminMobileApp'));
const AdminBbps             = lazy(() => import('@/pages/admin/AdminBbps'));
const AdminWallet           = lazy(() => import('@/pages/admin/AdminWallet'));
const AdminSubsidyTracker   = lazy(() => import('@/pages/admin/AdminSubsidyTracker'));
const AdminDeletionRequests = lazy(() => import('@/pages/admin/AdminDeletionRequests'));
const AdminActionView       = lazy(() => import('@/pages/admin/AdminActionView'));
const AdminApplicationDetail= lazy(() => import('@/pages/admin/AdminApplicationDetail'));
const AdminPaymentsReport   = lazy(() => import('@/pages/admin/AdminPaymentsReport'));
const AdminVendorLedger     = lazy(() => import('@/pages/admin/AdminVendorLedger'));
const AdminReports          = lazy(() => import('@/pages/admin/AdminReports'));
const AdminLandingCMS       = lazy(() => import('@/pages/admin/AdminLandingCMS'));
const AdminSitePages        = lazy(() => import('@/pages/admin/AdminSitePages'));
const AdminBlog             = lazy(() => import('@/pages/admin/AdminBlog'));
const AdminCustomerStories  = lazy(() => import('@/pages/admin/AdminCustomerStories'));
const AdminTeam             = lazy(() => import('@/pages/admin/AdminTeam'));
const AdminPendingSignups   = lazy(() => import('@/pages/admin/AdminPendingSignups'));
const AdminLoans            = lazy(() => import('@/pages/admin/AdminLoans'));
const AdminEkoSettings      = lazy(() => import('@/pages/admin/AdminEkoSettings'));
const AdminRedPaySettings   = lazy(() => import('@/pages/admin/AdminRedPaySettings'));
const AdminProviderRouter   = lazy(() => import('@/pages/admin/AdminProviderRouter'));
const AdminFinancialReports = lazy(() => import('@/pages/admin/AdminFinancialReports'));
const AdminCreditReports    = lazy(() => import('@/pages/admin/AdminCreditReports'));
const AdminWhatsAppBroadcast= lazy(() => import('@/pages/admin/AdminWhatsAppBroadcast'));
const AdminWappTemplates    = lazy(() => import('@/pages/admin/AdminWappTemplates'));
const AdminSlaConfig        = lazy(() => import('@/pages/admin/AdminSlaConfig'));
const AdminLoginEvents      = lazy(() => import('@/pages/admin/AdminLoginEvents'));
const AdminPushConsole      = lazy(() => import('@/pages/admin/AdminPushConsole'));
const AdminBroadcastNews    = lazy(() => import('@/pages/admin/AdminBroadcastNews'));
const AdminHierarchy        = lazy(() => import('@/pages/admin/AdminHierarchy'));
const MyTeamPage            = lazy(() => import('@/pages/admin/MyTeamPage'));
const VendorTeam            = lazy(() => import('@/pages/vendor/VendorTeam'));
const VendorMyFirm          = lazy(() => import('@/pages/vendor/VendorMyFirm'));
const VendorWallet          = lazy(() => import('@/pages/vendor/VendorWallet'));
const VendorKhata           = lazy(() => import('@/pages/vendor/VendorKhata'));
const MyStaffWallet         = lazy(() => import('@/pages/admin/MyStaffWallet'));
const TerritoryHeatmap      = lazy(() => import('@/pages/admin/TerritoryHeatmap'));
const AdminVendorCoverage   = lazy(() => import('@/pages/admin/AdminVendorCoverage'));
const AdminCustomerLedger   = lazy(() => import('@/pages/admin/AdminCustomerLedger'));
const MyAttendanceHistory   = lazy(() => import('@/pages/staff/MyAttendanceHistory'));
const StaffAttendance       = lazy(() => import('@/pages/staff/StaffAttendance'));

import '@/App.css';

/**
 * PageFallback — universal Suspense loader used while a route bundle is
 * being fetched. Keeps the frame from going blank on slow networks;
 * shows just a spinner (skeletons live inside each individual page so
 * they can reflect the page's real layout).
 */
function PageFallback() {
    return (
        <div className='min-h-[60vh] grid place-items-center' data-testid='route-suspense-loader'>
            <div className='flex flex-col items-center gap-2 text-muted-foreground'>
                <Loader2 className='w-6 h-6 animate-spin text-primary' />
                <div className='text-xs'>Loading…</div>
            </div>
        </div>
    );
}

function RoleGuard({ role, children }) {
    const { user } = useAuth();
    usePushNotifications();
    if (!user) return <Navigate to={role === 'admin' ? '/admin/login' : '/customer/login'} replace />;
    if (role === 'customer' && user.role !== 'customer') return <Navigate to='/' replace />;
    if (role === 'admin' && !['admin', 'sales', 'care', 'installation', 'vendor', 'saathi'].includes(user.role)) return <Navigate to='/' replace />;
    return <><AttendanceGate />{children}</>;
}

/**
 * RootRoute
 * ─────────────────────────────────────────────────────────────
 * On the public website (any regular browser) → renders the original
 * marketing <Landing /> page unchanged.
 *
 * Inside the Capacitor Android/iOS shell (detected by `native-app` class
 * added in `src/index.js`) → renders the app-first <MobileWelcome />
 * screen with clear Customer vs Admin/Staff entry points.
 *
 * If a user is already authenticated we route them straight to their
 * role-scoped dashboard so they don't see the welcome/landing again.
 */
function RootRoute() {
    const { user } = useAuth();
    if (user) {
        if (user.role === 'customer') return <Navigate to='/customer' replace />;
        return <Navigate to='/admin' replace />;
    }
    const isNative = typeof document !== 'undefined'
        && document.documentElement.classList.contains('native-app');
    return isNative ? <MobileWelcome /> : <Landing />;
}

export default function App() {
    return (
        <LangProvider>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                    {/* Phase 47.29 — Update popups intentionally disabled at
                        the user's request. Version-info API remains for
                        admin visibility, but no banner / prompt renders. */}
                    {/* <InAppUpdateChecker /> */}
                    <Toaster
                        position='top-center'
                        richColors
                        closeButton
                        expand
                        visibleToasts={4}
                        duration={4500}
                        toastOptions={{
                            unstyled: false,
                            classNames: {
                                toast:
                                    'group !w-[min(94vw,440px)] !min-h-[68px] !p-4 !pr-10 ' +
                                    '!rounded-2xl !border-2 !shadow-2xl backdrop-blur-md ' +
                                    '!text-[15px] !leading-snug !font-medium ' +
                                    'transition-all data-[type=success]:!border-emerald-500/40 ' +
                                    'data-[type=error]:!border-red-500/40 ' +
                                    'data-[type=warning]:!border-amber-500/40 ' +
                                    'data-[type=info]:!border-sky-500/40',
                                title: '!text-[15px] !font-semibold !leading-tight',
                                description: '!text-[13px] !opacity-90 mt-0.5',
                                icon: '!w-6 !h-6 mr-1 shrink-0',
                                closeButton:
                                    '!w-7 !h-7 !rounded-full !border !text-[16px] ' +
                                    '!top-1/2 !-translate-y-1/2 !right-3 !left-auto',
                                success: '!bg-emerald-50/95 !text-emerald-950 dark:!bg-emerald-950/95 dark:!text-emerald-50',
                                error: '!bg-red-50/95 !text-red-950 dark:!bg-red-950/95 dark:!text-red-50',
                                warning: '!bg-amber-50/95 !text-amber-950 dark:!bg-amber-950/95 dark:!text-amber-50',
                                info: '!bg-sky-50/95 !text-sky-950 dark:!bg-sky-950/95 dark:!text-sky-50',
                            },
                        }}
                    />
                    {/* <AppUpdateBanner /> */}
                    {/* <InstallAppPrompt /> */}
                    <IdleSessionManager />
                    <Suspense fallback={<PageFallback />}>
                        <Routes>
                            <Route path='/' element={<RootRoute />} />
                            {/* Public multi-page website */}
                            <Route element={<SiteLayout />}>
                                <Route path='/residential-solar' element={<ResidentialSolarPage />} />
                                <Route path='/residential-solar/:type' element={<SolutionTypePage />} />
                                <Route path='/housing-society-solar' element={<SocietySolarPage />} />
                                <Route path='/commercial-solar' element={<CommercialSolarPage />} />
                                <Route path='/industrial-solar' element={<Navigate to='/commercial-solar' replace />} />
                                <Route path='/pm-surya-ghar-subsidy' element={<SubsidyGuidePage />} />
                                <Route path='/subsidy' element={<Navigate to='/pm-surya-ghar-subsidy' replace />} />
                                <Route path='/solar-calculator' element={<SolarCalculatorPage />} />
                                <Route path='/products' element={<ProductsPage />} />
                                <Route path='/products/:slug' element={<ProductDetailPage />} />
                                <Route path='/pricing' element={<PricingPage />} />
                                <Route path='/how-it-works' element={<HowItWorksPage />} />
                                <Route path='/warranty-amc' element={<WarrantyPage />} />
                                <Route path='/financing' element={<FinancingPage />} />
                                <Route path='/why-uppower' element={<WhyUPPowerPage />} />
                                <Route path='/about' element={<AboutPage />} />
                                <Route path='/contact' element={<ContactPage />} />
                                <Route path='/service-areas' element={<ServiceAreasPage />} />
                                <Route path='/faq' element={<FaqPage />} />
                                <Route path='/careers' element={<CareersPage />} />
                                <Route path='/blog' element={<BlogListPage />} />
                                <Route path='/blog/:slug' element={<BlogPostPage />} />
                                <Route path='/customer-stories' element={<CustomerStoriesPage />} />
                                <Route path='/solar-in/:slug' element={<CityPage />} />
                                <Route path='/service-areas/:slug' element={<CityPage />} />
                            </Route>
                            <Route path='/privacy' element={<Privacy />} />
                            <Route path='/privacy-policy' element={<Privacy />} />
                            <Route path='/customer/login' element={<CustomerAuth />} />
                            <Route path='/customer/pending-approval' element={<SignupPendingApproval />} />
                            <Route path='/customer/kyc' element={<SignupKycPage />} />
                            <Route path='/admin/login' element={<AdminAuth />} />

                            <Route path='/customer' element={<RoleGuard role='customer'><CustomerLayout /></RoleGuard>}>
                                <Route index element={<CustomerHome />} />
                                <Route path='home' element={<CustomerHome />} />
                                <Route path='apply' element={<NewApplication />} />
                                <Route path='applications' element={<MyApplications />} />
                                <Route path='services' element={<ServicesPage />} />
                                <Route path='applications/:id' element={<ApplicationDetail />} />
                                <Route path='support' element={<SupportPage />} />
                                <Route path='complaints' element={<MyComplaints />} />
                                <Route path='subsidy' element={<SubsidyInfo />} />
                                <Route path='links' element={<QuickLinks />} />
                                <Route path='vendors' element={<VendorList />} />
                                <Route path='vendors/:id/chat' element={<VendorChat />} />
                                <Route path='profile' element={<ProfilePage />} />
                                <Route path='learn' element={<SolarInfoPage />} />
                                <Route path='ai' element={<AIChatPage />} />
                                <Route path='quotations' element={<QuotationsPage />} />
                                <Route path='survey' element={<SiteSurveyPage />} />
                                <Route path='generation' element={<GenerationLogPage />} />
                                <Route path='refer' element={<ReferPage />} />
                                <Route path='projects' element={<CustomerProjects />} />
                                <Route path='projects/:id' element={<CustomerProjectDetail />} />
                                <Route path='credit-report' element={<CustomerCreditReport />} />
                                <Route path='bbps' element={<BbpsPage />} />
                                <Route path='wallet' element={<WalletPage />} />
                                <Route path='loans' element={<CustomerLoans />} />
                            </Route>

                            <Route path='/admin' element={<RoleGuard role='admin'><AdminLayout /></RoleGuard>}>
                                <Route index element={<AdminDashboard />} />
                                <Route path='dashboard' element={<AdminDashboard />} />
                                <Route path='leads' element={<AdminLeads />} />
                                <Route path='leads/config' element={<AdminLeadConfig />} />
                                <Route path='leads/:id' element={<AdminLeadDetail />} />
                                <Route path='quotations' element={<AdminQuotations />} />
                                <Route path='sale-invoices' element={<AdminSaleInvoices />} />
                                <Route path='payment-ins' element={<AdminPaymentIns />} />
                                <Route path='cash-register' element={<AdminCashRegister />} />
                                <Route path='payment-center' element={<AdminPaymentCenter />} />
                                <Route path='applications' element={<AdminApplications />} />
                                <Route path='applications/:id' element={<AdminApplicationDetail />} />
                                <Route path='projects' element={<AdminProjects />} />
                                <Route path='projects/:id' element={<AdminProjectDetail />} />
                                <Route path='credit-reports' element={<AdminCreditReports />} />
                                <Route path='credit-report' element={<CustomerCreditReport />} />
                                <Route path='bbps' element={<AdminBbps />} />
                                <Route path='wallet' element={<AdminWallet />} />
                                <Route path='complaints' element={<AdminComplaints />} />
                                {/* Phase 28 — /admin/vendors merged into Staff & Roles.
                                    Redirect keeps old bookmarks & deep-links working. */}
                                <Route path='vendors' element={<Navigate to='/admin/staff?tab=vendor' replace />} />
                                <Route path='care-audit-log' element={<AdminCareAuditLog />} />
                                <Route path='customers' element={<AdminCustomers />} />
                                <Route path='pending-signups' element={<AdminPendingSignups />} />
                                <Route path='loans' element={<AdminLoans />} />
                                <Route path='eko-settings' element={<AdminEkoSettings />} />
                                <Route path='redpay-settings' element={<AdminRedPaySettings />} />
                                <Route path='provider-router' element={<AdminProviderRouter />} />
                                <Route path='financial-reports' element={<AdminFinancialReports />} />
                                <Route path='subsidy' element={<AdminSubsidyEditor />} />
                                <Route path='content' element={<AdminContentEditor />} />
                                <Route path='site-surveys' element={<AdminSiteSurveys />} />
                                <Route path='staff' element={<AdminStaff />} />
                                <Route path='access-bundles' element={<AdminAccessBundles />} />
                                <Route path='referrals' element={<AdminReferrals />} />
                                <Route path='attendance' element={<AdminAttendance />} />
                                <Route path='attendance-summary' element={<AdminAttendanceSummary />} />
                                <Route path='products' element={<AdminProducts />} />
                                <Route path='payments-report' element={<AdminPaymentsReport />} />
                                <Route path='vendor-ledger' element={<AdminVendorLedger />} />
                                <Route path='reports' element={<AdminReports />} />
                                <Route path='landing-cms' element={<AdminLandingCMS />} />
                                <Route path='site-pages' element={<AdminSitePages />} />
                                <Route path='blog' element={<AdminBlog />} />
                                <Route path='customer-stories' element={<AdminCustomerStories />} />
                                <Route path='team' element={<AdminTeam />} />
                                <Route path='login-events' element={<AdminLoginEvents />} />
                                <Route path='push-console' element={<AdminPushConsole />} />
                                <Route path='broadcast-news' element={<AdminBroadcastNews />} />
                                <Route path='hierarchy' element={<AdminHierarchy />} />
                                <Route path='my-team' element={<MyTeamPage />} />
                                <Route path='vendor-team' element={<VendorTeam />} />
                                <Route path='my-firm' element={<VendorMyFirm />} />
                                <Route path='vendor-wallet' element={<VendorWallet />} />
                                <Route path='my-khata' element={<VendorKhata />} />
                                <Route path='my-wallet' element={<MyStaffWallet />} />
                                <Route path='territory-heatmap' element={<TerritoryHeatmap />} />
                                <Route path='vendor-coverage' element={<AdminVendorCoverage />} />
                                <Route path='customer-ledger' element={<AdminCustomerLedger />} />
                                <Route path='my-attendance-history' element={<MyAttendanceHistory />} />
                                <Route path='saathi-bookings' element={<AdminSaathiBookings />} />
                                <Route path='staff-tracking' element={<AdminStaffTracking />} />
                                <Route path='my-attendance' element={<StaffAttendance />} />
                                <Route path='notifications' element={<AdminNotifications />} />
                                <Route path='career-applications' element={<AdminCareerApplications />} />
                                <Route path='system-errors' element={<AdminSystemErrors />} />
                                <Route path='view-messages' element={<AdminMessageOutbox />} />
                                <Route path='whatsapp-inbox' element={<WhatsAppInbox />} />
                                <Route path='nerochat-logs' element={<AdminNerochatLogs />} />
                                <Route path='whatsapp-broadcast' element={<AdminWhatsAppBroadcast />} />
                                <Route path='wapp-templates' element={<AdminWappTemplates />} />
                                <Route path='sla-config' element={<AdminSlaConfig />} />
                                <Route path='razorpay' element={<AdminRazorpay />} />
                                <Route path='mobile-app' element={<AdminMobileApp />} />
                                <Route path='subsidy-tracker' element={<AdminSubsidyTracker />} />
                                <Route path='deletion-requests' element={<AdminDeletionRequests />} />
                                <Route path='action-view' element={<AdminActionView />} />
                            </Route>

                            <Route path='*' element={<Navigate to='/' replace />} />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </LangProvider>
    );
}
